/**
 * WhatsAppButton — shared wa.me click-to-chat button.
 *
 * 2026-02-19 · Added across every patient-facing document (Advance Receipt,
 * Sales Invoice, Service & Repair, Service Completion, Appointment, HA
 * Trial, Payment Receipt, Audiogram/Clinical Report).
 *
 * Rules (per 2026-02-19 spec):
 *   • Pure wa.me redirect — NO WhatsApp Business API, NO cost, NO config.
 *   • Opens WhatsApp Web in a new tab (clinic staff sends from their own
 *     logged-in WhatsApp session).
 *   • Phone normalisation: strip ALL non-numeric chars, drop leading `0`
 *     or `91` prefix, then prepend `91`.
 *   • If phone is missing → render DISABLED button with explanatory tooltip.
 *   • NO message logging / NO storage of outbound text.
 */
import React from 'react';
import { MessageCircle } from 'lucide-react';

/** Normalise an Indian mobile number into E.164 digits for wa.me.
 *  Returns an empty string when nothing usable remains.
 */
export function normalizeIndianMobile(raw) {
  if (!raw) return '';
  // 1. keep digits only
  let digits = String(raw).replace(/\D/g, '');
  if (!digits) return '';
  // 2. drop a single leading zero (common Indian landline/mobile notation)
  if (digits.startsWith('0')) digits = digits.replace(/^0+/, '');
  // 3. drop an existing 91 country code so we can re-prepend it uniformly
  if (digits.length > 10 && digits.startsWith('91')) {
    digits = digits.slice(2);
  }
  // 4. anything other than exactly 10 digits is suspicious — return as-is
  //    with the 91 prefix so wa.me can at least try. Validation is a
  //    data-quality concern, not a UI concern.
  return `91${digits}`;
}

/** Build the full wa.me URL — exported for tests + direct use. */
export function buildWaLink(mobile, message) {
  const num = normalizeIndianMobile(mobile);
  if (!num) return null;
  const text = message ? `?text=${encodeURIComponent(message)}` : '';
  return `https://wa.me/${num}${text}`;
}

/** Open wa.me in a new tab. Returns true if opened, false if no phone. */
export function openWhatsApp(mobile, message) {
  const url = buildWaLink(mobile, message);
  if (!url) return false;
  window.open(url, '_blank', 'noopener,noreferrer');
  return true;
}

/**
 * Props:
 *   mobile    — raw patient phone (any format)
 *   message   — pre-filled text (plain string; we URL-encode)
 *   label     — button label ("WhatsApp" default). Pass `null` for icon-only.
 *   size      — "sm" | "md" (default "sm")
 *   testid    — data-testid override
 *   className — extra Tailwind classes
 */
export default function WhatsAppButton({
  mobile,
  message,
  label = 'WhatsApp',
  size = 'sm',
  testid = 'whatsapp-btn',
  className = '',
}) {
  const hasPhone = !!normalizeIndianMobile(mobile);
  const sizeCls = size === 'md'
    ? 'px-3 py-1.5 text-xs'
    : 'px-2 py-1 text-[11px]';
  const base = 'inline-flex items-center gap-1 font-semibold rounded shadow-sm transition-colors';
  const enabled = 'bg-[#25D366] hover:bg-[#1ebe5a] text-white';
  const disabled = 'bg-slate-200 text-slate-400 cursor-not-allowed';

  const onClick = (e) => {
    e?.stopPropagation?.();
    if (!hasPhone) return;
    openWhatsApp(mobile, message);
  };

  const title = hasPhone
    ? 'Send via WhatsApp'
    : 'No phone number on file for this patient';

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={!hasPhone}
      aria-disabled={!hasPhone}
      title={title}
      data-testid={testid}
      className={`${base} ${sizeCls} ${hasPhone ? enabled : disabled} ${className}`}
    >
      <MessageCircle size={size === 'md' ? 14 : 12} />
      {label}
    </button>
  );
}
